<template>
  <div class="flex flex-col gap-6 h-full max-h-full">
    
    <div class="flex-none p-6 pt-12 pb-2">
        <div class="flex flex-col md:flex-row md:justify-between items-start md:items-center gap-4 md:gap-0">
          
          <!-- Top Row: Title & Button (Mobile/Desktop) -->
          <div class="flex flex-row justify-between w-full md:w-auto items-start md:items-center">
            <div class="flex flex-col gap-1 text-left">
              <h2 class="text-3xl font-bold text-gray-900 dark:text-white">{{ $t('dashboard.explorePlaylists') }}</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400">{{ $t('dashboard.explorePlaylistsDesc') }}</p>
            </div>
            
            <div class="md:hidden mt-1">
                <Tooltip position="left" :text="$t('tooltip.addPlaylist')">
                    <PlusCircleIcon class="w-8 h-8 text-gray-700 dark:text-gray-300 cursor-pointer hover:text-black dark:hover:text-white transition-colors" @click="addPlaylist" />
                </Tooltip>
            </div>
          </div>

          <!-- Filters Row -->
          <div class="flex flex-col md:flex-row gap-4 items-center w-full md:w-auto">
              <div class="w-full md:w-auto overflow-x-auto">
                  <PlaylistFilterBar
                      :search="search"
                      :filter="filter"
                      @update:search="search = $event"
                      @update:filter="setFilter($event)"
                      />
              </div>
              <div class="hidden md:block">
                  <Tooltip position="left" :text="$t('tooltip.addPlaylist')">
                      <PlusCircleIcon class="w-8 h-8 text-gray-700 dark:text-gray-300 cursor-pointer hover:text-black dark:hover:text-white transition-colors" @click="addPlaylist" />
                  </Tooltip>
              </div>
          </div>
        </div>
    </div>
    <div ref="scrollContainer" @scroll="checkScroll" class="flex-1 pl-6 pr-6 scroll-smooth min-h-[100px]" :class="{'overflow-none': loading, 'overflow-auto': !loading}">
        <div class="flex flex-col gap-6 min-h-[100px]">
            <Loading v-if="loading" class="overflow-none" />
            
            <template v-if="searchingDb">
                <div v-for="i in 3" :key="'search-skeleton-' + i" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 flex flex-row items-center gap-4 relative border border-gray-100 dark:border-gray-700 animate-pulse">
                    <div class="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg flex-shrink-0"></div>
                    <div class="flex flex-col gap-2 flex-grow">
                        <div class="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                        <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                    </div>
                </div>
            </template>
            
            <template v-else>
                <PlaylistCard
                    v-for="playlist in displayPlaylists"
                    :key="playlist.id"
                    :playlist="playlist"
                    :updatingFavorite="playlist.updatingFavorite"
                    @toggle-favorite="toggleFavorite(playlist)"
                />
            </template>

            <!-- List Skeleton shimmers while loading more -->
            <template v-if="loadingMore">
                <div v-for="i in 3" :key="'skeleton-' + i" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 flex flex-row items-center gap-4 relative border border-gray-100 dark:border-gray-700 animate-pulse">
                    <div class="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg flex-shrink-0"></div>
                    <div class="flex flex-col gap-2 flex-grow">
                        <div class="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                        <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                    </div>
                </div>
            </template>
            <div v-if="!loading && !searchingDb && displayPlaylists.length === 0" class="flex flex-col items-center justify-center py-16 gap-4 text-center">
                <div class="w-20 h-20 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center">
                    <svg class="w-10 h-10 text-gray-300 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                </div>
                <div>
                    <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">{{ $t('playlist.noPlaylistsFound') }}</h3>
                    <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ $t('dashboard.startCreatingPlaylist') }}</p>
                </div>
                <button class="mt-2 px-6 py-2 bg-gray-900 dark:bg-white hover:bg-black dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-full font-medium transition-colors shadow-sm flex items-center gap-2" @click="addPlaylist">
                    <PlusCircleIcon class="w-5 h-5" />
                    {{ $t('dashboard.create') }}
                </button>
            </div>
        </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, watch, defineEmits } from 'vue'
import PlaylistCard from '../../components/playlist/PlaylistCard.vue'
import PlaylistFilterBar from '../../components/playlist/PlaylistFilterBar.vue'
import { PlusCircleIcon } from '@heroicons/vue/24/outline'
import PlaylistService from '../../service/playlist.service.js'
import toastStore from '../../store/toast.store.js';
import { useRouter } from 'vue-router';
import debounce from 'lodash.debounce'

const props = defineProps({
    playlists: {
        type: Array,
        required: true
    },
    loading: {
        type: Boolean,
        required: true
    },
    hasMore: {
        type: Boolean,
        default: false
    },
    loadingMore: {
        type: Boolean,
        default: false
    },
    favoriteIds: {
        type: Object,
        default: () => new Set()
    }
})

const emit = defineEmits(['load-more'])

const playlists = ref(props.playlists)
const loading = ref(props.loading)
const search = ref('')
const filter = ref('favorite')
const router = useRouter();
const scrollContainer = ref(null)
const dbSearchResults = ref([])
const searchingDb = ref(false)

const performDbSearch = debounce(async (query) => {
    if (!query) {
        dbSearchResults.value = [];
        return;
    }
    if (filteredPlaylists.value.length > 0) {
        dbSearchResults.value = [];
        return;
    }
    searchingDb.value = true;
    try {
        const res = await PlaylistService.fetchPaginated({ page: 1, size: 10, search: query });
        const results = res?.data || [];
        results.forEach(p => {
            p.isFavorite = props.favoriteIds.has(p.id) || props.favoriteIds.has(p._id);
        });
        dbSearchResults.value = results;
    } catch (e) {
        console.error("DB Search failed:", e);
        dbSearchResults.value = [];
    } finally {
        searchingDb.value = false;
    }
}, 300);

watch(search, (newVal) => {
    performDbSearch(newVal);
});

const checkScroll = () => {
    if (!scrollContainer.value) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value;
    if (props.hasMore && scrollTop >= scrollHeight - clientHeight - 100) {
        emit('load-more');
    }
}


const filteredPlaylists = computed(() => {
  let result = playlists.value
  if (filter.value === 'favorite') {
    result = result.filter(p => p.isFavorite)
  } else if (filter.value === 'newest') {
    result = [...result].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  }
  if (search.value) {
    result = result.filter(p => p.name && p.name.toLowerCase().includes(search.value.toLowerCase()))
  }
  return result
})

const displayPlaylists = computed(() => {
  if (search.value && filteredPlaylists.value.length === 0) {
    return dbSearchResults.value;
  }
  return filteredPlaylists.value;
})

const sortPlaylists = (list) => {
    return list.sort( (a,b) => {
    if(a.isFavorite) {
        return -1; 
    } else {
        if(b.isFavorite) {
            return 1;
        } else {
            return 0
        }
    }
  })
}

function toggleFavorite(playlist) {
    playlist.updatingFavorite=true;
    const playlistId = playlist._id || playlist.id;
    PlaylistService.toggleFavorite(playlistId).then(() => {
        playlist.isFavorite = !playlist.isFavorite
        if (playlist.isFavorite) {
            props.favoriteIds.add(playlistId);
        } else {
            props.favoriteIds.delete(playlistId);
        }
        playlists.value = sortPlaylists(playlists.value);
        toastStore.dispatch('addMessage', {message: 'Playlist updated', icon: 'success'})
    }).catch(() => {
        toastStore.dispatch('addMessage', {message: 'Failed to toggle favorite', icon: 'error'})
    }).finally(() =>{
        playlist.updatingFavorite=false;
    })
}

function addPlaylist() {
  router.push({path: '/playlist/new'})
}

function setFilter(value) {
  filter.value = value
}

const getPlaylistKeys = (p) => {
    if (!p) return [];
    const keys = [];
    if (p._id) keys.push(String(p._id));
    if (p.id) keys.push(String(p.id));
    if (p.uuid) keys.push(String(p.uuid));
    return keys;
};

const arePlaylistsEqual = (p1, p2) => {
    if (!p1 || !p2) return false;
    const k1 = getPlaylistKeys(p1);
    const k2 = getPlaylistKeys(p2);
    if (k1.some(k => k2.includes(k))) return true;
    if (p1.name && p2.name && p1.name.trim().toLowerCase() === p2.name.trim().toLowerCase()) return true;
    return false;
};

const deduplicatePlaylists = (playlistArray) => {
    const result = [];
    (playlistArray || []).forEach(p => {
        if (!p) return;
        const exists = result.some(item => arePlaylistsEqual(item, p));
        if (!exists) {
            result.push(p);
        }
    });
    return result;
};

watch(() => props.playlists, (newPlaylists) => {
  playlists.value = deduplicatePlaylists(newPlaylists || []);
}, { immediate: true });

watch(() => props.loading, (newLoading) => {
  loading.value = newLoading
});

</script> 